import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Science, Autorenew, RateReview, Verified, Inventory2, WorkspacePremium,
} from '@mui/icons-material';
import { StatCard } from '../dashboard/StatCard';
import { TEST_STATUS_COLORS } from '../../constants/testStatusColors';
import type { TestListingRow } from '../../services/testListingService';

interface TestsKpiRowProps {
  rows: TestListingRow[];
  /** Archive totals across ALL tests (null while loading). */
  archiveTotal: number | null;
  issuedTotal: number | null;
  /** Clicking a status card toggles that status filter. */
  onStatusClick: (statuses: string[]) => void;
  onArchiveClick: () => void;
}

/** Lab-admin KPI strip: pipeline counts for the CURRENT filter result set +
 *  report-archive totals across the whole lab. */
export const TestsKpiRow = ({ rows, archiveTotal, issuedTotal, onStatusClick, onArchiveClick }: TestsKpiRowProps) => {
  const theme = useTheme();
  const count = (status: string) => rows.filter((r) => r.TestStatus === status).length;
  const inReview = count('Request Review') + count('Review Completed');

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
        gap: 1,
        mb: 1.5,
      }}
    >
      <StatCard
        dense icon={<Science />} label="Tests (shown)" value={rows.length}
        color={theme.palette.primary.main}
        onClick={() => onStatusClick([])}
      />
      <StatCard
        dense icon={<Autorenew />} label="In Progress" value={count('In Progress')}
        color={TEST_STATUS_COLORS['In Progress']}
        onClick={() => onStatusClick(['In Progress'])}
      />
      <StatCard
        dense icon={<RateReview />} label="In Review" value={inReview}
        color={TEST_STATUS_COLORS['Request Review']}
        onClick={() => onStatusClick(['Request Review', 'Review Completed'])}
      />
      <StatCard
        dense icon={<Verified />} label="Approved" value={count('Approved')}
        color={TEST_STATUS_COLORS.Approved}
        onClick={() => onStatusClick(['Approved'])}
      />
      <StatCard
        dense icon={<Inventory2 />} label="Reports archived" value={archiveTotal ?? 0}
        color={theme.schedule.machineTime}
        onClick={onArchiveClick}
      />
      <StatCard
        dense icon={<WorkspacePremium />} label="Issued copies" value={issuedTotal ?? 0}
        color={theme.customColors.maroon}
        onClick={onArchiveClick}
      />
    </Box>
  );
};
